import { useQuery } from '@tanstack/react-query'
import { BadgePercent, LifeBuoy, Link2, type LucideIcon, Megaphone, Search, UserPlus } from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { http } from '@/lib/api/client'
import type { CustomerList, Paginated, StaffRole, TicketSummary } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { visibleNav } from './nav'

interface Item {
  group: string
  label: string
  hint?: string
  icon?: LucideIcon
  mono?: string
  to: string
}

/** Waits until typing pauses before searching. */
function useDebounced(value: string, ms = 200) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

/**
 * ⌘K: jump to any page, find a customer or a ticket, or start a common task. Customers and
 * tickets come from the same searches as their pages.
 */
export function CommandPalette({ onClose, role }: { onClose: () => void; role: StaffRole }) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const term = useDebounced(q.trim())
  const searching = term.length >= 2

  const customers = useQuery({
    queryKey: ['palette', 'customers', term],
    queryFn: () => http.get<CustomerList>('/customers', { search: term, page: 1 }),
    enabled: searching,
    staleTime: 30_000,
  })
  const tickets = useQuery({
    queryKey: ['palette', 'tickets', term],
    queryFn: () =>
      http.get<Paginated<TicketSummary>>('/tickets', {
        status: 'all',
        assigned: 'all',
        source: 'all',
        search: term,
        page: 1,
      }),
    enabled: searching,
    staleTime: 30_000,
  })

  const items = useMemo<Item[]>(() => {
    const needle = q.trim().toLowerCase()
    const pages: Item[] = visibleNav(role)
      .flatMap((g) => g.items)
      .filter((i) => !i.external)
      .map((i) => ({ group: 'Go to', label: i.label, icon: i.icon, to: i.to }))
    const admin = role === 'owner' || role === 'admin'
    const actions: Item[] = [
      ...(admin
        ? [{ group: 'Do', label: 'New discount code', icon: BadgePercent, to: '/discounts?new=1' }]
        : []),
      ...(role === 'owner'
        ? [{ group: 'Do', label: 'Invite a staff member', icon: UserPlus, to: '/team?invite=1' }]
        : []),
      ...(admin ? [{ group: 'Do', label: 'Change the announcement', icon: Megaphone, to: '/settings' }] : []),
      { group: 'Do', label: 'Make a checkout link for a customer', icon: Link2, to: '/customers' },
    ]
    if (!needle) return [...actions, ...pages.slice(0, 6)]
    const match = (i: Item) => i.label.toLowerCase().includes(needle)
    const found: Item[] = [
      ...(customers.data?.data ?? []).slice(0, 6).map((c) => ({
        group: 'Customers',
        label: c.name,
        hint: [c.owner?.name, c.plan.name].filter(Boolean).join(' · '),
        mono: c.name.trim().charAt(0).toUpperCase() || '?',
        to: `/customers/${c.id}`,
      })),
      ...(tickets.data?.data ?? []).slice(0, 4).map((t) => ({
        group: 'Tickets',
        label: `${t.reference} ${t.subject}`,
        hint: t.workspace?.name ?? t.requester?.company ?? t.requester?.name ?? undefined,
        icon: LifeBuoy,
        to: `/tickets/${t.id}`,
      })),
    ]
    return [...found, ...pages.filter(match), ...actions.filter(match)]
  }, [q, role, customers.data, tickets.data])

  useEffect(() => input.current?.focus(), [])

  const open = (i: number) => {
    const item = items[i]
    if (!item) return
    onClose()
    void navigate(item.to)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose()
    else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => (items.length ? (a + 1) % items.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => (items.length ? (a - 1 + items.length) % items.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      open(active)
    }
  }

  let lastGroup = ''
  const rows: ReactNode[] = []
  items.forEach((item, i) => {
    if (item.group !== lastGroup) {
      rows.push(
        <div key={`g-${item.group}`} className="pal-g">
          {item.group}
        </div>,
      )
      lastGroup = item.group
    }
    const Icon = item.icon
    rows.push(
      <button
        key={`${item.group}-${item.to}-${i}`}
        type="button"
        role="option"
        aria-selected={i === active}
        className={cn('pal-i', i === active && 'on')}
        onMouseEnter={() => setActive(i)}
        onClick={() => open(i)}
      >
        <span className="ic">
          {item.mono ? (
            <span style={{ fontFamily: 'var(--f-display)', fontWeight: 600 }}>{item.mono}</span>
          ) : Icon ? (
            <Icon aria-hidden="true" />
          ) : null}
        </span>
        <span className="min-w-0 truncate">{item.label}</span>
        {item.hint && <small>{item.hint}</small>}
      </button>,
    )
  })

  return (
    <>
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Search and commands"
        onKeyDown={onKeyDown}
      >
        <div className="pal-in">
          <Search aria-hidden="true" />
          <input
            ref={input}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setActive(0)
            }}
            placeholder="Search customers, tickets and pages, or type a command"
            aria-label="Search"
            autoComplete="off"
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-results"
          />
          <span className="kbd">
            <kbd>Esc</kbd>
          </span>
        </div>
        <div className="pal-res" id="palette-results" role="listbox">
          {rows.length ? (
            rows
          ) : (
            <p className="empty-line" style={{ padding: 16 }}>
              {searching && (customers.isFetching || tickets.isFetching)
                ? 'Searching…'
                : `Nothing matches “${q}”.`}
            </p>
          )}
        </div>
        <div className="pal-foot">
          <span className="kbd">
            <kbd>↑</kbd>
            <kbd>↓</kbd> move
          </span>
          <span className="kbd">
            <kbd>Enter</kbd> open
          </span>
          <span className="kbd">
            <kbd>/</kbd> search from anywhere
          </span>
        </div>
      </div>
    </>
  )
}
